import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ViewModeToggle, useViewMode } from '../components/ViewModeToggle';
import { 
  FileCheck, 
  Download, 
  Eye, 
  Search, 
  Calendar,
  Loader2,
  ArrowLeft,
  FileText,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isWithinInterval, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import PdfStorageNotice from '../components/field-report/PdfStorageNotice';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ApprovedReports() {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [viewMode, setViewMode] = useViewMode('approvedReports.viewMode');
  
  // Reports state
  const [preCons, setPreCons] = useState([]);
  const [softDigs, setSoftDigs] = useState([]);
  const [locates, setLocates] = useState([]);

  const loadReports = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [preconsRes, softDigsRes, locatesRes] = await Promise.all([
        axios.get(`${API}/precons`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/soft-digs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/locates`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      // Filter only completed/approved reports
      setPreCons((preconsRes.data || []).filter(r => r.status === 'completed'));
      setSoftDigs((softDigsRes.data || []).filter(r => r.status === 'completed'));
      setLocates((locatesRes.data || []).filter(r => r.status === 'completed'));
    } catch (e) {
      console.error('Failed to load reports:', e);
      toast.error('Failed to load approved reports');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Download PDF
  const downloadPdf = async (type, id, jobNumber) => {
    const endpoints = { precon: 'precons', softdig: 'soft-digs', locate: 'locates' };
    const pdfUrl = `${API}/${endpoints[type]}/${id}/pdf`;
    
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(pdfUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${jobNumber || 'Report'}_${type}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (e) {
      toast.error('Failed to download PDF');
    }
  };

  // Filter by date range
  const filterByDate = (reports) => {
    if (dateFilter === 'all') return reports;
    
    const now = new Date();
    let startDate, endDate;
    
    switch (dateFilter) {
      case 'last7':
        startDate = subDays(now, 7);
        endDate = now;
        break;
      case 'last30':
        startDate = subDays(now, 30);
        endDate = now;
        break;
      case 'thisMonth':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'lastMonth':
        const lastMonth = subMonths(now, 1);
        startDate = startOfMonth(lastMonth);
        endDate = endOfMonth(lastMonth);
        break;
      case 'custom':
        if (!customDateFrom || !customDateTo) return reports;
        startDate = parseISO(customDateFrom);
        endDate = parseISO(customDateTo);
        break;
      default:
        return reports;
    }
    
    return reports.filter(r => {
      const reportDate = r.completed_at ? parseISO(r.completed_at) : (r.updated_at ? parseISO(r.updated_at) : parseISO(r.created_at));
      return isWithinInterval(reportDate, { start: startDate, end: endDate });
    });
  };

  // Filter by search term (job number, project name, location)
  const filterBySearch = (reports) => {
    if (!searchTerm.trim()) return reports;
    const term = searchTerm.toLowerCase();
    return reports.filter(r => 
      (r.job_number?.toLowerCase().includes(term)) ||
      (r.project_name?.toLowerCase().includes(term)) ||
      (r.site_location?.toLowerCase().includes(term)) ||
      (r.soft_dig_number?.toLowerCase().includes(term))
    );
  };

  // Combined filter
  const applyFilters = (reports) => {
    return filterBySearch(filterByDate(reports));
  };

  // Get all reports combined and sorted
  const getAllReports = () => {
    const all = [
      ...preCons.map(r => ({ ...r, type: 'precon', typeName: 'PreCon' })),
      ...softDigs.map(r => ({ ...r, type: 'softdig', typeName: 'Soft Dig' })),
      ...locates.map(r => ({ ...r, type: 'locate', typeName: 'Locate' }))
    ];
    return all.sort((a, b) => {
      const dateA = a.completed_at || a.updated_at || a.created_at;
      const dateB = b.completed_at || b.updated_at || b.created_at;
      return new Date(dateB) - new Date(dateA);
    });
  };

  const filteredPreCons = applyFilters(preCons);
  const filteredSoftDigs = applyFilters(softDigs);
  const filteredLocates = applyFilters(locates);
  const filteredAll = applyFilters(getAllReports());

  // Render report card
  const renderReportCard = (report, type) => (
    <Card 
      key={report.id} 
      className="hover:shadow-md transition-shadow cursor-pointer" 
      onClick={() => navigate(`/${type === 'softdig' ? 'soft-dig' : type}/${report.id}?view=true`)}
      data-testid={`approved-report-card-${report.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {report.job_number && (
                <Badge variant="outline" className="font-mono text-amber-600 border-amber-300">{report.job_number}</Badge>
              )}
              <Badge className="bg-blue-100 text-blue-700">
                {report.typeName || (type === 'precon' ? 'PreCon' : type === 'softdig' ? 'Soft Dig' : 'Locate')}
              </Badge>
              <Badge className="bg-green-100 text-green-700">
                Approved
              </Badge>
            </div>
            <h3 className="font-semibold text-slate-900">
              {report.project_name || report.soft_dig_number || report.site_location || 'No Title'}
            </h3>
            <p className="text-sm text-slate-500">{report.site_location || 'No Location'}</p>
            <div className="text-xs text-slate-400 mt-1 space-y-0.5">
              <p>Approved: {report.completed_at ? format(parseISO(report.completed_at), 'MMM d, yyyy') : '-'}</p>
              {report.approved_by_name && <p>By: {report.approved_by_name}</p>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1"
            onClick={(e) => { e.stopPropagation(); navigate(`/${type === 'softdig' ? 'soft-dig' : type}/${report.id}?view=true`); }}
            data-testid={`view-report-btn-${report.id}`}
          >
            <Eye className="w-3 h-3 mr-1" />
            View
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 text-amber-600 border-amber-300 hover:bg-amber-50"
            onClick={(e) => {
              e.stopPropagation();
              const previewPath = type === 'softdig' ? 'soft-dig' : type;
              navigate(`/${previewPath}/${report.id}/preview`);
            }}
            data-testid={`download-pdf-btn-${report.id}`}
            title="Open PDF preview (Print / Save PDF from the preview page)"
          >
            <Download className="w-3 h-3 mr-1" />
            PDF
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render report list row (compact list view)
  const renderReportRow = (report, type) => {
    const typeLabel = report.typeName || (type === 'precon' ? 'PreCon' : type === 'softdig' ? 'Soft Dig' : 'Locate');
    return (
      <div
        key={report.id}
        className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
        onClick={() => navigate(`/${type === 'softdig' ? 'soft-dig' : type}/${report.id}?view=true`)}
        data-testid={`approved-report-row-${report.id}`}
      >
        <Badge className="bg-blue-100 text-blue-700 flex-shrink-0 w-20 justify-center">{typeLabel}</Badge>
        <Badge className="bg-green-100 text-green-700 flex-shrink-0 w-20 justify-center">Approved</Badge>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">
            {report.project_name || report.soft_dig_number || report.site_location || 'No Title'}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {[
              report.job_number,
              report.completed_at ? format(parseISO(report.completed_at), 'MMM d, yyyy') : '',
              report.site_location
            ].filter(Boolean).join(' • ')}
          </p>
        </div>
        <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/${type === 'softdig' ? 'soft-dig' : type}/${report.id}?view=true`)} title="View"><Eye className="w-4 h-4" /></Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const previewPath = type === 'softdig' ? 'soft-dig' : type;
              navigate(`/${previewPath}/${report.id}/preview`);
            }}
            title="Open PDF preview (Print / Save PDF from the preview page)"
          >
            <Download className="w-4 h-4 text-amber-600" />
          </Button>
        </div>
      </div>
    );
  };

  // Render body — switches between Cards grid and List
  const renderBody = (items, getType) => {
    if (viewMode === 'list') {
      return (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {items.map((r) => renderReportRow(r, typeof getType === 'function' ? getType(r) : getType))}
            </div>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((r) => renderReportCard(r, typeof getType === 'function' ? getType(r) : getType))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approved-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/field-reports')} className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Barlow_Condensed'] flex items-center gap-2">
              <FileCheck className="w-7 h-7 text-green-600" />
              Approved Reports
            </h1>
            <p className="text-slate-500 text-sm">All completed and approved field reports</p>
          </div>
        </div>
        <Badge className="bg-green-100 text-green-700 text-lg py-1 px-3">
          {preCons.length + softDigs.length + locates.length} Total
        </Badge>
      </div>

      <PdfStorageNotice />

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by job #, project name, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            {/* Date Filter */}
            <div className="w-full md:w-48">
              <Label className="text-xs text-slate-500 mb-1 block">Date Range</Label>
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger data-testid="date-filter-select">
                  <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="last7">Last 7 Days</SelectItem>
                  <SelectItem value="last30">Last 30 Days</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                  <SelectItem value="lastMonth">Last Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* View toggle */}
            <div className="w-full md:w-auto flex flex-col">
              <Label className="text-xs text-slate-500 mb-1 block">View</Label>
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
            </div>
            
            {/* Custom Date Range */}
            {dateFilter === 'custom' && (
              <>
                <div className="w-full md:w-36">
                  <Label className="text-xs text-slate-500 mb-1 block">From</Label>
                  <Input 
                    type="date" 
                    value={customDateFrom} 
                    onChange={(e) => setCustomDateFrom(e.target.value)}
                    data-testid="date-from-input"
                  />
                </div>
                <div className="w-full md:w-36">
                  <Label className="text-xs text-slate-500 mb-1 block">To</Label>
                  <Input 
                    type="date" 
                    value={customDateTo} 
                    onChange={(e) => setCustomDateTo(e.target.value)}
                    data-testid="date-to-input"
                  />
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs by Report Type */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="all" data-testid="tab-all">
            All ({filteredAll.length})
          </TabsTrigger>
          <TabsTrigger value="precon" data-testid="tab-precon">
            PreCon ({filteredPreCons.length})
          </TabsTrigger>
          <TabsTrigger value="softdig" data-testid="tab-softdig">
            Soft Dig ({filteredSoftDigs.length})
          </TabsTrigger>
          <TabsTrigger value="locate" data-testid="tab-locate">
            Locate ({filteredLocates.length})
          </TabsTrigger>
        </TabsList>

        {/* All Reports Tab */}
        <TabsContent value="all" className="mt-4">
          {filteredAll.length === 0 ? (
            <Card className="p-8 text-center">
              <FileCheck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No Approved Reports Found</h3>
              <p className="text-slate-500">
                {searchTerm || dateFilter !== 'all' 
                  ? 'Try adjusting your search or date filters'
                  : 'Reports will appear here once they are approved'}
              </p>
            </Card>
          ) : (
            renderBody(filteredAll, (r) => r.type)
          )}
        </TabsContent>

        {/* PreCon Tab */}
        <TabsContent value="precon" className="mt-4">
          {filteredPreCons.length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No Approved PreCon Reports</h3>
              <p className="text-slate-500">
                {searchTerm || dateFilter !== 'all' 
                  ? 'Try adjusting your search or date filters'
                  : 'PreCon reports will appear here once approved'}
              </p>
            </Card>
          ) : (
            renderBody(filteredPreCons, 'precon')
          )}
        </TabsContent>

        {/* Soft Dig Tab */}
        <TabsContent value="softdig" className="mt-4">
          {filteredSoftDigs.length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No Approved Soft Dig Reports</h3>
              <p className="text-slate-500">
                {searchTerm || dateFilter !== 'all' 
                  ? 'Try adjusting your search or date filters'
                  : 'Soft Dig reports will appear here once approved'}
              </p>
            </Card>
          ) : (
            renderBody(filteredSoftDigs, 'softdig')
          )}
        </TabsContent>

        {/* Locate Tab */}
        <TabsContent value="locate" className="mt-4">
          {filteredLocates.length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">No Approved Locate Reports</h3>
              <p className="text-slate-500">
                {searchTerm || dateFilter !== 'all' 
                  ? 'Try adjusting your search or date filters'
                  : 'Locate reports will appear here once approved'}
              </p>
            </Card>
          ) : (
            renderBody(filteredLocates, 'locate')
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
